import { TimeRange, IntegerRange, TimePeriod } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';

/**
 * JS Utils for the Java class TimeRange
 */
export default class TimeRangeUtils {
    /**
     * return the time duration, in minutes, for [from, to[
     * @return
     */
    static rangeDurationInMinutes(timeRange: TimeRange): number {
        return TimeUtils.diffInMinutes(timeRange.to, timeRange.from);
    }

    /**
     * Return the index of the time step for timeRange date.
     * The index is computed as a continuum, so that a date 't'
     *   * slightly larger than getFrom() will return 0;
     *   * slightly smaller than getFrom() will return -1;
     * @param t
     * @return
     */
    static getStep(timeRange: TimeRange, t: Date): number {
        const diff = t.getTime() - timeRange.from.getTime();
        const idx = Math.floor(diff / (TimeUtils.MIN_TO_MS * timeRange.stepSizeInMinutes));
        return diff >= 0 ? idx : idx - 1;
    }

    /**
     * Return the index of the time step that is just before timeRange date 't'.
     * For example, if we have [10h00, 12h00[ with interval=20min, then
     * getStepBefore(11h00) -> 2 (i.e. 10h40 step)
     * getStepBefore(11h02) -> 3 (i.e. 11h00 step)
     * @param t
     * @return
     */
    static getStepBefore(timeRange: TimeRange, t: Date): number {
        const diff = t.getTime() - timeRange.from.getTime();
        const rem = diff % (TimeUtils.MIN_TO_MS * timeRange.stepSizeInMinutes);
        const idx = TimeRangeUtils.getStep(timeRange, t);
        return rem === 0 ? idx - 1 : idx;
    }

    /**
     * return the index range of time steps that include the given data in their counting periods
     * @param date
     * @return
     */
    static getStepsThatCountDate(timeRange: TimeRange, date: Date, bound: boolean): IntegerRange {
        const indx2 = TimeRangeUtils.getStep(timeRange, date);
        const indx1 = indx2 - timeRange.countDurationInMinutes / timeRange.stepSizeInMinutes + 1;

        if (bound) {
            if (indx2 >= 0 && indx1 < TimeRangeUtils.getNumberOfSteps(timeRange)) {
                return {
                    start: TimeRangeUtils.boundedStep(timeRange, indx1),
                    end: TimeRangeUtils.boundedStep(timeRange, indx2),
                };
            } else {
                return null;
            }
        } else {
            return { start: indx1, end: indx2 };
        }
    }

    /**
     * restrict the given index to the [min, max] bounds of the time range.
     * @param index
     * @return
     */
    static boundedStep(timeRange: TimeRange, index: number): number {
        if (index < 0) return 0;
        const max = TimeRangeUtils.getNumberOfSteps(timeRange) - 1;
        return index <= max ? index : max;
    }

    /**
     * return true if the index is within the [min,max[ bounds of the time range
     * @param i
     * @return
     */
    static isStepInRange(timeRange: TimeRange, index: number): boolean {
        return index >= 0 && index < TimeRangeUtils.getNumberOfSteps(timeRange);
    }

    /**
     * return the time step at index 'index'.
     * @param index
     * @return
     */
    static getTimeAtStep(timeRange: TimeRange, index: number): Date {
        return TimeUtils.addMinutes(timeRange.from, index * timeRange.stepSizeInMinutes);
    }

    /**
     * Return the step's time period at index 'index'
     * @param index
     * @return
     */
    static getTimePeriodAtStep(timeRange: TimeRange, index: number): TimePeriod {
        return {
            from: TimeRangeUtils.getTimeAtStep(timeRange, index),
            to: TimeRangeUtils.getTimeAtStep(timeRange, index + 1),
        };
    }

    /**
     * Return the counting period at step index 'index'
     * @param index
     * @return
     */
    static getCountingPeriodAtStep(timeRange: TimeRange, index: number): TimePeriod {
        const time = TimeRangeUtils.getTimeAtStep(timeRange, index);
        return { from: time, to: TimeUtils.addMinutes(time, timeRange.countDurationInMinutes) };
    }

    /**
     * Return [from, to[ as a TimePeriod.
     * @return
     */
    static getTimePeriod(timeRange: TimeRange): TimePeriod {
        return { from: timeRange.from, to: timeRange.to };
    }

    /**
     * Return the total counting period of all the 'bars' combined.
     * @return
     */
    static getCountingTimePeriod(timeRange: TimeRange): TimePeriod {
        const time = TimeRangeUtils.getTimeAtStep(timeRange, TimeRangeUtils.getNumberOfSteps(timeRange) - 1);
        return { from: timeRange.from, to: TimeUtils.addMinutes(time, timeRange.countDurationInMinutes) };
    }

    /**
     * Return the number of steps (i.e. count bars) in timeRange time range.
     * @return
     */
    static getNumberOfSteps(timeRange: TimeRange): number {
        return TimeUtils.diffInMinutes(timeRange.to, timeRange.from) / timeRange.stepSizeInMinutes;
    }

    /**
     * Adjust the [from, to[ time period, to align on step boundaries.
     * @return
     */
    static realignTimePeriodIfNeeded(timeRange: TimeRange): TimeRange {
        const from2 = TimeUtils.nearestLowerMinuteBoundary(timeRange.from, timeRange.stepSizeInMinutes);
        const to2 = TimeUtils.nearestUpperMinuteBoundary(timeRange.to, timeRange.stepSizeInMinutes);
        return timeRange.from.getTime() === from2.getTime() && timeRange.to.getTime() === to2.getTime()
            ? timeRange
            : {
                  from: from2,
                  to: to2,
                  stepSizeInMinutes: timeRange.stepSizeInMinutes,
                  countDurationInMinutes: timeRange.countDurationInMinutes,
              };
    }
}
